import {
  CONNECTING_POINT_RADIUS,
  MAX_VIEW_SCALE,
  MIN_VIEW_SCALE,
} from '../../constants';
import { isLineInsideRect, isRectsIntersecting } from '../../helpers/intersect';
import { copy, getClosestCornerById } from '../../helpers/utility';
import { createUpdateChange } from '../../services/Actions';
import {
  ClientCoordinates,
  SelectionCoordinates,
  SelectionMode,
  SelectionModes,
  ViewBox,
} from '../../types';

import { ElementType, Element as IElement, Point } from '@ritabygglov/core';

export const setupResizeElement = (
  e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  element: IElement,
  setSelectionCoordinates: (
    value: React.SetStateAction<SelectionCoordinates>
  ) => void,
  selectionCoordinates: SelectionCoordinates,
  setSelectionMode: (value: React.SetStateAction<SelectionMode>) => void,
  selectionMode: SelectionMode,
  clientCoordinates: ClientCoordinates
) => {
  if (!(e.target instanceof Element)) return;
  const {
    x: xOffset,
    y: yOffset,
    width,
    height,
  } = e.target?.parentElement?.children[0].getBoundingClientRect() || {
    x: null,
    y: null,
  };
  if (xOffset === null || yOffset === null) return;
  if (width === undefined || height === undefined) return;

  const selectedAnchor = getClosestCornerById(element, e.target.id);
  if (selectedAnchor === null) return;
  setSelectionCoordinates({
    ...selectionCoordinates,
    xOffset,
    yOffset,
    initialX: clientCoordinates.x,
    initialY: clientCoordinates.y,
    initialWidth: width,
    initialHeight: height,
    selectedAnchor,
  });
  setSelectionMode({
    ...selectionMode,
    elementType: element.type,
    type: SelectionModes.Resizing,
  });
};

export const setupRotateElement = (
  e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  element: IElement,
  setSelectionCoordinates: (
    value: React.SetStateAction<SelectionCoordinates>
  ) => void,
  selectionCoordinates: SelectionCoordinates,
  setSelectionMode: (value: React.SetStateAction<SelectionMode>) => void,
  selectionMode: SelectionMode,
  scale: number
) => {
  if (!(e.target instanceof Element)) return;
  const { width, height } =
    e.target?.parentElement?.children[0].getBoundingClientRect() || {
      x: null,
      y: null,
    };
  if (!(width && height)) return;
  const initialX = e.clientX * scale; // TODO: Strange that we don't just use the client coordinates here?
  const initialY = e.clientY * scale;
  setSelectionCoordinates({
    // TODO: Remove this setSelectionCoordinates from here, this should happen in mouseEvents already.
    ...selectionCoordinates,
    initialX,
    initialY,
    initialWidth: width,
    initialHeight: height,
  });
  setSelectionMode({
    ...selectionMode,
    elementType: element.type,
    type: SelectionModes.Rotating,
  });
};

export const setupMovingElement = (
  e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  originElements: IElement[],
  setSelectionCoordinates: (
    value: React.SetStateAction<SelectionCoordinates>
  ) => void,
  selectionCoordinates: SelectionCoordinates,
  scale: number
) => {
  if (!(e.target instanceof Element)) return;

  // NOT SURE ABOUT THIS ONE? SHOULD BE SCALED AS WELL I BELIEVE
  const { x: xOffset, y: yOffset } = e.target.getBoundingClientRect();
  const initialX = e.clientX * scale - xOffset * scale;
  const initialY = e.clientY * scale - yOffset * scale;
  setSelectionCoordinates({
    ...selectionCoordinates,
    initialX,
    initialY,
    startX: e.clientX * scale,
    startY: e.clientY * scale,
    originElements,
  });
};

export const findSelectedElements = (
  elements: {
    [id: string]: IElement;
  },
  startX: number,
  startY: number,
  currentX: number,
  currentY: number
): string[] => {
  const selectRect = {
    left: Math.min(startX, currentX),
    top: Math.min(startY, currentY),
    right: Math.max(startX, currentX),
    bottom: Math.max(startY, currentY),
  };
  return Object.keys(elements).filter((elementId) => {
    const element = elements[elementId];
    switch (element.type) {
      case ElementType.Polyline: {
        if (element.points.length > 1) {
          // Consider all points in the line
          return isLineInsideRect(
            element.points[0].x,
            element.points[0].y,
            element.points[1].x,
            element.points[1].y,
            selectRect
          );
        } else if (element.points.length === 1) {
          return isLineInsideRect(
            element.points[0].x,
            element.points[0].y,
            element.points[0].x,
            element.points[0].y,
            selectRect
          );
        } else {
          return false;
        }
      }
      case ElementType.Rect:
      case ElementType.Text:
      case ElementType.Symbol:
      case ElementType.Fixture:
      case ElementType.Triangle:
      case ElementType.Trapezoid:
      case ElementType.Angle:
      case ElementType.Group:
        return isRectsIntersecting(selectRect, {
          left: element.x,
          top: element.y,
          right: element.x + element.width,
          bottom: element.y + element.height,
        });
      default:
        return false;
    }
  });
};

export const updateElementCoords = (
  element: IElement,
  diffX: number,
  diffY: number,
  originElement: IElement
): IElement => {
  const obj = copy(element);

  if (
    obj.type === ElementType.Rect &&
    originElement.type === ElementType.Rect
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Text &&
    originElement.type === ElementType.Text
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Polyline &&
    originElement.type === ElementType.Polyline
  ) {
    const newPoints = originElement.points.map((v) => {
      const newX = v.x + diffX;
      const newY = v.y + diffY;
      return { ...v, x: newX, y: newY };
    });
    obj.points = newPoints;
  } else if (
    obj.type === ElementType.Symbol &&
    originElement.type === ElementType.Symbol
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Fixture &&
    originElement.type === ElementType.Fixture
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Triangle &&
    originElement.type === ElementType.Triangle
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Trapezoid &&
    originElement.type === ElementType.Trapezoid
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Angle &&
    originElement.type === ElementType.Angle
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else if (
    obj.type === ElementType.Group &&
    originElement.type === ElementType.Group
  ) {
    obj.x = originElement.x + diffX;
    obj.y = originElement.y + diffY;
  } else {
    throw new Error('Something went wrong in set element coords');
  }

  return obj;
};

export const setElementCoords = (
  element: IElement,
  diffX: number,
  diffY: number,
  originElement: IElement,
  currentUserId: string
) => {
  const updatedElement = updateElementCoords(
    element,
    diffX,
    diffY,
    originElement
  );

  return createUpdateChange(updatedElement, true, currentUserId);
};

const distance = (p1: Point, p2: Point) =>
  Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

/**
 * Finds overlapping point as long as its not within 10px of prev point
 * @param current
 * @param points
 * @returns
 */
export function findOverlappingPoint(current: number, points: Point[]) {
  if (!points[current]) return -1;
  return points.findIndex(
    (p, i) =>
      distance(points[current], p) <= CONNECTING_POINT_RADIUS && i < current - 1
  );
}

/**
 * Unlinks a point from the earlier point it was joined to, leaving its x/y at
 * the current position. Mutates the passed points array.
 */
function releaseSelfSnap(target: Point, points: Point[]): void {
  if (target.connectingPointIndex !== undefined) {
    const connectedTo = points[target.connectingPointIndex];
    if (connectedTo) connectedTo.cornerRadius = 0;
  }
  target.connectingPointIndex = undefined;
}

/**
 * Self-snaps points[pos] onto an overlapping earlier point: links it via
 * connectingPointIndex AND syncs its x/y to the target so the stored
 * coordinates match what is rendered (no visual-only snap). When there is no
 * overlap the link is released. Mutates the passed points array. Returns
 * whether it snapped.
 *
 * Pass isSnappingEnabled=false (meta held) to opt out. Note that this still
 * has to release an existing link rather than return early: syncConnectedPoints
 * would otherwise pull the point straight back onto the one it joined.
 */
export function applySelfSnap(
  pos: number,
  points: Point[],
  isSnappingEnabled = true
): boolean {
  const target = points[pos];
  if (!target) return false;

  if (!isSnappingEnabled) {
    releaseSelfSnap(target, points);
    return false;
  }

  const targetIndex = findOverlappingPoint(pos, points);
  if (targetIndex === -1) {
    releaseSelfSnap(target, points);
    return false;
  }

  const snapTo = points[targetIndex];
  target.x = snapTo.x;
  target.y = snapTo.y;
  target.connectingPointIndex = targetIndex;
  return true;
}

/**
 * Keeps joined points truthful: copies each connected point's coordinates from
 * its target. connectingPointIndex always references an earlier point, so a
 * single forward pass resolves every link. Mutates the passed points array.
 */
export function syncConnectedPoints(points: Point[]): void {
  points.forEach((point) => {
    if (point.connectingPointIndex === undefined) return;
    const target = points[point.connectingPointIndex];
    if (!target) return;
    point.x = target.x;
    point.y = target.y;
  });
}

export const getClientCoordinates = (
  e: React.MouseEvent<SVGSVGElement, MouseEvent> | MouseEvent,
  viewBox: ViewBox
): ClientCoordinates => {
  const { clientX, clientY } = e;
  return {
    x: clientX * viewBox.scale + viewBox.x,
    y: clientY * viewBox.scale + viewBox.y,
  };
};

export const getClosestElementId = (
  e: React.MouseEvent<SVGSVGElement, MouseEvent>
) => {
  if (!(e.target instanceof Element)) return;

  let id = e.target.id;
  const parentElementId = e.target.parentElement?.id;
  if (!id && parentElementId) {
    id = parentElementId;
  }
  return id;
};

const ZOOM_STEP = 1.05;

export const getViewBoxAfterZoom = (
  viewBox: ViewBox,
  offsetX: number,
  offsetY: number,
  deltaY: number,
  zoomStep: number = ZOOM_STEP
) => {
  const w = window.innerWidth;
  const h = window.innerHeight;

  const currentScale = viewBox.w / w;
  const direction = Math.sign(deltaY);
  const factor = direction > 0 ? zoomStep : direction < 0 ? 1 / zoomStep : 1;
  const clamped = Math.min(
    MAX_VIEW_SCALE,
    Math.max(MIN_VIEW_SCALE, currentScale * factor)
  );
  // Clamp only in the direction of travel, so a view box that is already
  // outside the range stays put rather than being yanked the other way.
  const nextScale =
    factor < 1
      ? Math.min(clamped, currentScale)
      : Math.max(clamped, currentScale);
  // 1 when the view box already sits at a zoom limit.
  const applied = nextScale / currentScale;

  const newW = viewBox.w * applied;
  const newH = viewBox.h * applied;

  // Keep the point under the cursor fixed in canvas coordinates.
  return {
    x: viewBox.x + (offsetX / w) * (viewBox.w - newW),
    y: viewBox.y + (offsetY / h) * (viewBox.h - newH),
    w: newW,
    h: newH,
    scale: nextScale,
  };
};

export const isPointSame = (
  p1?: Pick<Point, 'x' | 'y'>,
  p2?: Pick<Point, 'x' | 'y'>
) => {
  if (!p1 || !p2) return false;

  if (p1.x === p2.x && p1.y === p2.y) {
    return true;
  }
  return false;
};
export const nudgeMove = (
  direction: string,
  scale: number,
  nudgeAmount: number
) => {
  let dx = 0;
  let dy = 0;

  const scaledIncrement = nudgeAmount * scale;

  if (direction == 'ArrowUp') {
    dy = -scaledIncrement;
  } else if (direction == 'ArrowDown') {
    dy = scaledIncrement;
  } else if (direction == 'ArrowLeft') {
    dx = -scaledIncrement;
  } else if (direction == 'ArrowRight') {
    dx = scaledIncrement;
  }

  return { dx, dy };
};

export const measureTextContentHeight = (
  elementId: string
): number | undefined => {
  const textarea = document.querySelector<HTMLTextAreaElement>(
    `textarea[id="${CSS.escape(elementId)}"]`
  );
  return textarea ? textarea.scrollHeight : undefined;
};
